import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, onSnapshot, query, serverTimestamp, where } from 'firebase/firestore';
import { appId, db } from './firebase-config';
import {
  resolveRoutesCollectionPath,
  resolveStockControlsCollectionPath,
  resolveStockMovementsCollectionPath
} from './core/company/companyPaths';
import CompanyPlanPanel from './CompanyPlanPanel';
import {
  formatRemainingDaysLabel,
  formatShortDate,
  resolveCommercialState,
  resolveCompanyPlanMeta,
} from './companyPlans';

const Icon = ({ path, className = 'w-6 h-6' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const MODULE_DEFINITIONS = [
  { key: 'logistics', label: 'Logística' },
  { key: 'service', label: 'Servicio' },
  { key: 'routeMonitor', label: 'Monitor en tiempo real' },
  { key: 'reports', label: 'Reportes y liquidación' },
  { key: 'fleetExpenses', label: 'Gastos de Flota' },
  { key: 'stock', label: 'Stock y movimientos' },
  { key: 'systemGuide', label: 'Guía y soporte' },
];

const DEFAULT_MODULES = MODULE_DEFINITIONS.reduce((acc, module) => {
  acc[module.key] = { enabled: true };
  return acc;
}, {});

const VIEW_TO_MODULE_KEY = {
  stock: 'stock',
  logistics: 'logistics',
  service: 'service',
  monitor: 'routeMonitor',
  reports: 'reports',
  fleetExpenses: 'fleetExpenses',
  systemGuide: 'systemGuide',
};

const ACTIVE_ROUTE_STATUSES = new Set([
  'active', 'activa', 'activo', 'in_progress', 'in-progress', 'en_curso', 'en-curso',
  'started', 'pending', 'pendiente', 'assigned', 'asignada', 'asignado',
  'programmed', 'programada', 'programado',
]);

const DAY_MS = 24 * 60 * 60 * 1000;
const DASHBOARD_AUTO_COLLAPSE_MS = 1000;
const DASHBOARD_INTRO_STORAGE_KEY = 'gcentra_intro_inicio_seen';
const normalizeIntroCompanyKey = (companyId = 'default') => String(companyId || 'default').trim() || 'default';
const buildDashboardIntroStorageKey = (companyId) => `${DASHBOARD_INTRO_STORAGE_KEY}:${normalizeIntroCompanyKey(companyId)}`;

const readDashboardIntroSeen = (companyId) => {
  if (typeof window === 'undefined') return true;
  const scopedKey = buildDashboardIntroStorageKey(companyId);
  if (window.localStorage.getItem(scopedKey) === 'true') return true;

  // Preserve the old global flag only for the original default company.
  return normalizeIntroCompanyKey(companyId) === 'default'
    && window.localStorage.getItem(DASHBOARD_INTRO_STORAGE_KEY) === 'true';
};

const saveDashboardIntroSeen = (companyId, seen = true) => {
  if (typeof window === 'undefined') return;
  const value = seen ? 'true' : 'false';
  window.localStorage.setItem(buildDashboardIntroStorageKey(companyId), value);
  if (normalizeIntroCompanyKey(companyId) === 'default') {
    window.localStorage.setItem(DASHBOARD_INTRO_STORAGE_KEY, value);
  }
};
const INTRO_AUTO_ADVANCE_SECONDS = 3;
const INTRO_AMBIENT_TRACK = '/tutorials/music/gcentra-intro-long-v1.mp3';
const INTRO_FALLBACK_AUDIO = '/tutorials/intro/01-bienvenida.mp3';
const INTRO_NARRATION_TEXT = 'Bienvenidos a G-CENTRA. Una plataforma pensada para ordenar logística, servicios, seguimiento operativo, control de flota, stock y lectura económica en un solo lugar. Hoy la operación se apoya en siete módulos principales, en este orden: Logística, Servicio, Monitor en tiempo real, Reportes y liquidación, Gastos de Flota, Stock y movimientos, y Guía y soporte.';
const ELEVENLABS_DEFAULT_VOICE_ID = 'JBFqnCBsd6RMkjVDRZzb';
const ELEVENLABS_DEFAULT_MODEL_ID = 'eleven_multilingual_v2';
const INTRO_MODULE_CARDS = [
  { title: 'Logística', desc: 'Armás unidades, preparás rutas y organizás la salida diaria.', tone: 'from-sky-500/18 to-cyan-400/8', delay: '1.2s' },
  { title: 'Servicio', desc: 'Tomás pedidos, contratos y servicios programados.', tone: 'from-violet-500/18 to-purple-400/8', delay: '1.35s' },
  { title: 'Monitor en tiempo real', desc: 'Seguís rutas activas y ves qué unidad está en movimiento.', tone: 'from-emerald-500/18 to-cyan-400/8', delay: '1.5s' },
  { title: 'Reportes y liquidación', desc: 'Leés cierres, historial, gastos, ventas y diferencia.', tone: 'from-fuchsia-500/18 to-pink-400/8', delay: '2s' },
  { title: 'Gastos de Flota', desc: 'Registrás combustible, service y costos de cada transporte.', tone: 'from-amber-500/18 to-orange-400/8', delay: '2.25s' },
  { title: 'Stock y movimientos', desc: 'Registrás compras, controlás cantidades y preparás salidas.', tone: 'from-blue-500/18 to-sky-400/8', delay: '2.5s' },
  { title: 'Guía y soporte', desc: 'Tenés recorrido guiado, contexto y ayuda para avanzar sin perderte.', tone: 'from-slate-500/18 to-slate-300/8', delay: '2.75s' },
];

const buildIntroNarrationAudio = async ({ apiKey, voiceId, modelId, text }) => {
  if (!apiKey || !text) return null;

  const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'xi-api-key': apiKey,
    },
    body: JSON.stringify({
      text,
      model_id: modelId,
      language_code: 'es',
      output_format: 'mp3_44100_128',
      voice_settings: {
        stability: 0.45,
        similarity_boost: 0.78,
        style: 0.18,
        use_speaker_boost: true,
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`ElevenLabs respondió ${response.status}`);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

const createDashboardIntroAmbient = () => {
  if (typeof window === 'undefined') return null;

  const audio = new Audio(INTRO_AMBIENT_TRACK);
  audio.loop = true;
  audio.volume = 0.26;

  return {
    resume: () => audio.play().then(() => true).catch(() => false),
    stop: () => {
      audio.pause();
      audio.currentTime = 0;
    },
  };
};

const getLocalDateAsString = (date = new Date()) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const getRouteSortTime = (route) => {
  return Math.max(
    getTimestampMillis(route?.updatedAt),
    getTimestampMillis(route?.lastUpdatedAt),
    getTimestampMillis(route?.createdAt),
    route?.date ? new Date(`${route.date}T00:00:00`).getTime() : 0
  );
};

const sortRoutesDesc = (routes = []) => [...routes].sort((a, b) => getRouteSortTime(b) - getRouteSortTime(a));

const isRouteActive = (route) => {
  if (!route || typeof route !== 'object') return false;
  if (route.active === true || route.isActive === true) return true;

  const normalizedStatus = String(route.status || route.state || '').trim().toLowerCase();
  return ACTIVE_ROUTE_STATUSES.has(normalizedStatus);
};

const getEnabledModules = (companyModules = null) => {
  const source = companyModules && typeof companyModules === 'object' ? companyModules : DEFAULT_MODULES;
  return MODULE_DEFINITIONS
    .filter((module) => source?.[module.key]?.enabled !== false)
    .map((module) => ({
      key: module.key,
      label: module.label,
      enabled: source?.[module.key]?.enabled !== false,
    }));
};

const isViewEnabled = (companyModules = null, viewKey) => {
  const moduleKey = VIEW_TO_MODULE_KEY[viewKey];
  if (!moduleKey) return true;
  return companyModules?.[moduleKey]?.enabled !== false;
};

const ModuleCard = ({
  title,
  description,
  iconPath,
  colorClasses,
  onClick,
  id,
  badge,
  shimmerDelay = 0,
  disabled = false,
  disabledReason = '',
  active = false,
  activeLabel = '',
}) => (
  <button
    id={id}
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`relative min-h-[214px] overflow-hidden rounded-[2rem] p-5 text-left shadow-lg transition-all duration-300 group ${
      disabled
        ? 'cursor-not-allowed border border-slate-200 bg-slate-200/60 opacity-70'
        : `${active ? 'ring-2 ring-emerald-300/80 ring-offset-2 ring-offset-slate-100' : ''} ${colorClasses.bg} hover:-translate-y-1.5 hover:shadow-[0_24px_54px_rgba(15,23,42,0.24)]`
    }`}
  >
    {!disabled ? (
      <>
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.10),transparent_18%)]" />
        <div className="pointer-events-none absolute inset-y-0 left-[-35%] w-1/3 skew-x-[-18deg] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.46),transparent)] opacity-0 transition-all duration-700 group-hover:left-[120%] group-hover:opacity-100" />
        {active ? <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.18),transparent_40%),linear-gradient(180deg,rgba(16,185,129,0.14),transparent_28%)]" /> : null}
      </>
    ) : null}
    <div
      className={`absolute -top-6 -right-6 h-24 w-24 rounded-full opacity-20 transition-transform duration-500 ${
        disabled ? 'bg-slate-400' : `${colorClasses.iconBg} group-hover:scale-[2.4]}`
      }`}
    />

    <div className="relative z-10 flex h-full flex-col justify-start gap-5">
      <div className="flex items-start justify-between gap-3">
        <div className={`relative overflow-hidden rounded-[1.15rem] p-3 shadow-[0_10px_24px_rgba(15,23,42,0.14)] ${disabled ? 'bg-slate-300 text-slate-600' : `${colorClasses.iconBg} ring-1 ring-white/25`}`}>
          {!disabled ? <span className="pointer-events-none absolute inset-0 rounded-[1.15rem] bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.52),transparent_44%)]" /> : null}
          {!disabled ? (
            <span
              className="pointer-events-none absolute inset-y-0 left-0 w-1/2 skew-x-[-18deg] rounded-[1.15rem] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.78),transparent)]"
              style={{
                animation: 'dashboardIconSweep 18s ease-in-out infinite',
                animationDelay: `${shimmerDelay}s`,
              }}
            />
          ) : null}
          <Icon path={iconPath} className={`w-10 h-10 ${disabled ? 'text-slate-600' : colorClasses.iconText}`} />
        </div>

        {badge && (
          <span
            className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
              disabled ? 'bg-slate-300 text-slate-700' : active ? 'border border-emerald-300/35 bg-emerald-400/18 text-emerald-50 shadow-[0_0_18px_rgba(16,185,129,0.18)]' : 'bg-white/20 text-white'
            }`}
          >
            {badge}
          </span>
        )}
      </div>

      <div>
        <h2 className={`text-[1.05rem] font-black tracking-[-0.02em] ${disabled ? 'text-slate-700' : colorClasses.titleText}`}>{title}</h2>
        <p className={`mt-2 text-[13px] leading-5 ${disabled ? 'text-slate-500' : colorClasses.descriptionText}`}>{description}</p>
        {!disabled && active && activeLabel ? <p className="mt-3 text-xs font-black uppercase tracking-[0.16em] text-emerald-50/95">{activeLabel}</p> : null}
        {disabled && disabledReason ? <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{disabledReason}</p> : null}
      </div>
    </div>
  </button>
);

const SplitModuleCard = ({
  left,
  right,
  id,
}) => {
  const renderHalf = (side, module) => {
    const { disabled, disabledReason, active, activeLabel, onClick, shimmerDelay = 0 } = module;
    const roundedClass = side === 'left' ? 'rounded-l-[2rem] rounded-r-none' : 'rounded-r-[2rem] rounded-l-none';

    return (
      <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={`group relative min-h-[214px] overflow-hidden p-5 text-left transition-all duration-300 ${
          side === 'right' ? 'border-l border-white/20' : ''
        } ${
          disabled
            ? 'cursor-not-allowed bg-slate-200/70 opacity-75'
            : `${active ? 'ring-2 ring-emerald-300/70 ring-inset' : ''} ${module.colorClasses.bg} hover:z-10 hover:-translate-y-1 hover:shadow-[0_24px_54px_rgba(15,23,42,0.24)]`
        } ${roundedClass}`}
      >
        {!disabled ? (
          <>
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.10),transparent_18%)]" />
            <div className="pointer-events-none absolute inset-y-0 left-[-35%] w-1/3 skew-x-[-18deg] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.46),transparent)] opacity-0 transition-all duration-700 group-hover:left-[120%] group-hover:opacity-100" />
            {active ? <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.18),transparent_40%),linear-gradient(180deg,rgba(16,185,129,0.14),transparent_28%)]" /> : null}
          </>
        ) : null}
        <div
          className={`absolute -top-6 -right-6 h-24 w-24 rounded-full opacity-20 transition-transform duration-500 ${
            disabled ? 'bg-slate-400' : `${module.colorClasses.iconBg} group-hover:scale-[2.4]`
          }`}
        />

        <div className="relative z-10 flex h-full flex-col justify-start gap-5">
          <div className="flex items-start justify-between gap-3">
            <div className={`relative overflow-hidden rounded-[1.15rem] p-3 shadow-[0_10px_24px_rgba(15,23,42,0.14)] ${disabled ? 'bg-slate-300 text-slate-600' : `${module.colorClasses.iconBg} ring-1 ring-white/25`}`}>
              {!disabled ? <span className="pointer-events-none absolute inset-0 rounded-[1.15rem] bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.52),transparent_44%)]" /> : null}
              {!disabled ? (
                <span
                  className="pointer-events-none absolute inset-y-0 left-0 w-1/2 skew-x-[-18deg] rounded-[1.15rem] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.78),transparent)]"
                  style={{
                    animation: 'dashboardIconSweep 18s ease-in-out infinite',
                    animationDelay: `${shimmerDelay}s`,
                  }}
                />
              ) : null}
              <Icon path={module.iconPath} className={`w-10 h-10 ${disabled ? 'text-slate-600' : module.colorClasses.iconText}`} />
            </div>

            {module.badge ? (
              <span
                className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
                  disabled ? 'bg-slate-300 text-slate-700' : active ? 'border border-emerald-300/35 bg-emerald-400/18 text-emerald-50 shadow-[0_0_18px_rgba(16,185,129,0.18)]' : 'bg-white/20 text-white'
                }`}
              >
                {module.badge}
              </span>
            ) : null}
          </div>

          <div>
            <h2 className={`text-[1.05rem] font-black tracking-[-0.02em] ${disabled ? 'text-slate-700' : module.colorClasses.titleText}`}>{module.title}</h2>
            <p className={`mt-2 text-[13px] leading-5 ${disabled ? 'text-slate-500' : module.colorClasses.descriptionText}`}>{disabled ? 'Esta herramienta no está disponible en este momento.' : module.description}</p>
            {!disabled && active && activeLabel ? <p className="mt-3 text-xs font-black uppercase tracking-[0.16em] text-emerald-50/95">{activeLabel}</p> : null}
            {disabled && disabledReason ? <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{disabledReason}</p> : null}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div
      id={id}
      className="relative overflow-hidden rounded-[2rem] border border-white/35 bg-white/20 shadow-lg backdrop-blur-sm md:col-span-2 xl:col-span-1"
    >
      <div className="grid h-full min-h-[214px] grid-cols-1 md:grid-cols-2 xl:grid-cols-2">
        {renderHalf('left', left)}
        {renderHalf('right', right)}
      </div>
    </div>
  );
};

const SummaryCard = ({ title, value, subtitle, iconPath, tone = 'slate', onClick = null, valueClassName = '' }) => {
  const tones = {
    slate: 'from-slate-50 to-slate-100 border-slate-200 text-slate-900',
    cyan: 'from-cyan-50 to-sky-100 border-cyan-200 text-slate-900',
    emerald: 'from-emerald-50 to-green-100 border-emerald-200 text-slate-900',
    violet: 'from-violet-50 to-indigo-100 border-violet-200 text-slate-900',
    amber: 'from-amber-50 to-orange-100 border-amber-200 text-slate-900',
    red: 'from-red-50 to-rose-100 border-red-200 text-slate-900',
  };

  const Container = onClick ? 'button' : 'div';

  return (
    <Container
      type={onClick ? 'button' : undefined}
      onClick={onClick || undefined}
      className={`rounded-3xl border bg-gradient-to-br px-4 py-3 shadow-sm transition ${tones[tone] || tones.slate} ${onClick ? 'text-left hover:-translate-y-0.5 hover:shadow-[0_16px_32px_rgba(15,23,42,0.10)] focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-medium text-slate-500">{title}</p>
          <p className={`mt-1 text-[1.45rem] font-black tracking-tight ${valueClassName || 'truncate'}`}>{value}</p>
          <p className="mt-1 text-[12px] leading-4 text-slate-500">{subtitle}</p>
        </div>
        <div className="rounded-2xl bg-slate-900 p-3 text-white shadow-lg">
          <Icon path={iconPath} className="w-5 h-5" />
        </div>
      </div>
    </Container>
  );
};

const getRouteStatusLabel = (statusValue = '') => {
  const normalized = String(statusValue || '').trim().toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ');
  if (!normalized || normalized === 'sin estado') return 'Sin estado';
  if (normalized.includes('complete') || normalized.includes('finaliz')) return 'Finalizada';
  if (normalized.includes('cancel')) return 'Cancelada';
  if (normalized.includes('pend')) return 'Pendiente';
  if (normalized.includes('progress') || normalized.includes('curso') || normalized.includes('active') || normalized.includes('activa') || normalized.includes('activo') || normalized.includes('started')) {
    return 'En curso';
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const RouteRow = ({ route }) => {
  const status = String(route?.status || 'sin_estado').toLowerCase();
  const transportLabel = route?.transporterName || route?.transport || 'Sin asignar';

  const statusStyles = status.includes('complete') || status.includes('complet')
    ? 'bg-emerald-100 text-emerald-700'
    : isRouteActive(route)
      ? 'bg-cyan-100 text-cyan-700'
      : 'bg-slate-100 text-slate-600';

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-2.5">
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-slate-800">{route?.name || 'Ruta sin nombre'}</p>
        <p className="mt-0.5 text-xs text-slate-500">
          {route?.date || 'sin fecha'} · {transportLabel}
        </p>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusStyles}`}>
        {getRouteStatusLabel(route?.status || 'sin estado')}
      </span>
    </div>
  );
};

const StockActivityRow = ({ control }) => {
  const timestamp = getTimestampMillis(control?.endTime);
  const dateLabel = timestamp
    ? new Date(timestamp).toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Sin fecha';

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-2.5">
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-slate-800">
          Control de stock · {control?.productName || 'Producto'}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">
          {dateLabel} · {control?.type === 'manual' ? 'Manual' : 'Archivo'}
        </p>
      </div>
      <span className="shrink-0 rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-sky-700">
        Stock
      </span>
    </div>
  );
};

const getStockMovementTimestamp = (movement) =>
  getTimestampMillis(movement?.appliedAt) ||
  getTimestampMillis(movement?.createdAt) ||
  getTimestampMillis(movement?.updatedAt);

const getStockMovementPresentation = (movement) => {
  const movementType = String(movement?.movementType || '').trim().toLowerCase();
  const productName = movement?.productNameSnapshot || movement?.productName || 'Producto';
  const routeName = movement?.routeCodeSnapshot || movement?.routeNameSnapshot || movement?.routeName || '';
  const destinationName = movement?.destinationNameSnapshot || movement?.customerNameSnapshot || movement?.supplierNameSnapshot || '';
  const quantity = Number(movement?.quantity || 0);
  const quantityLabel = quantity > 0 ? `${quantity} ${quantity === 1 ? 'unidad' : 'unidades'}` : '';

  if (movementType === 'stock_outbound_prepared') {
    return {
      title: `Pedido de salida preparado · ${productName}`,
      subtitle: [quantityLabel, destinationName].filter(Boolean).join(' · ') || 'Listo para entrega',
      badge: 'Salida',
      badgeClass: 'bg-amber-100 text-amber-700',
    };
  }

  if (movementType === 'stock_outbound_assigned_to_route') {
    return {
      title: `Salida asignada a ruta · ${productName}`,
      subtitle: [quantityLabel, routeName || destinationName].filter(Boolean).join(' · ') || 'Asignada a logística',
      badge: 'Ruta',
      badgeClass: 'bg-cyan-100 text-cyan-700',
    };
  }

  if (movementType === 'stock_outbound_started') {
    return {
      title: `Salida en tránsito · ${productName}`,
      subtitle: [quantityLabel, routeName || destinationName].filter(Boolean).join(' · ') || 'En camino',
      badge: 'En vivo',
      badgeClass: 'bg-indigo-100 text-indigo-700',
    };
  }

  if (movementType === 'stock_outbound_delivered') {
    return {
      title: `Entrega completada · ${productName}`,
      subtitle: [quantityLabel, destinationName || routeName].filter(Boolean).join(' · ') || 'Impacto aplicado',
      badge: 'Entregado',
      badgeClass: 'bg-emerald-100 text-emerald-700',
    };
  }

  return {
    title: `Movimiento de stock · ${productName}`,
    subtitle: [quantityLabel, destinationName || routeName].filter(Boolean).join(' · ') || 'Movimiento operativo registrado',
    badge: 'Stock',
    badgeClass: 'bg-sky-100 text-sky-700',
  };
};

const StockMovementRow = ({ movement }) => {
  const timestamp = getStockMovementTimestamp(movement);
  const dateLabel = timestamp
    ? new Date(timestamp).toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Sin fecha';
  const presentation = getStockMovementPresentation(movement);

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-2.5">
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-slate-800">{presentation.title}</p>
        <p className="mt-0.5 text-xs text-slate-500">
          {dateLabel} · {presentation.subtitle}
        </p>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${presentation.badgeClass}`}>
        {presentation.badge}
      </span>
    </div>
  );
};

const getRecentActivityTickerPresentation = (item) => {
  if (!item) {
    return {
      title: 'Sin actividad reciente',
      subtitle: 'Esperando nuevos movimientos operativos.',
      badge: 'Standby',
    };
  }

  if (item._kind === 'route') {
    return {
      title: item?.name || 'Ruta sin nombre',
      subtitle: [item?.date || 'sin fecha', item?.transporterName || item?.transport || 'Sin asignar']
        .filter(Boolean)
        .join(' · '),
      badge: getRouteStatusLabel(item?.status || 'sin estado'),
    };
  }

  if (item._kind === 'stock-movement') {
    const presentation = getStockMovementPresentation(item);
    return {
      title: presentation.title,
      subtitle: presentation.subtitle,
      badge: presentation.badge,
    };
  }

  const timestamp = getTimestampMillis(item?.endTime);
  return {
    title: `Control de stock · ${item?.productName || 'Producto'}`,
    subtitle: timestamp
      ? new Date(timestamp).toLocaleString('es-AR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'Sin fecha',
    badge: 'Stock',
  };
};

const CommercialHero = ({ commercialState, companyName }) => {
  const toneMap = {
    cyan: {
      shell: 'border-cyan-200 bg-gradient-to-r from-cyan-50 via-white to-sky-50',
      badge: 'border-cyan-200 bg-white text-cyan-700',
      primary: 'bg-cyan-600 text-white hover:bg-cyan-700',
      secondary: 'border-cyan-200 bg-white text-cyan-700 hover:bg-cyan-50',
      accent: 'text-cyan-700',
    },
    emerald: {
      shell: 'border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-green-50',
      badge: 'border-emerald-200 bg-white text-emerald-700',
      primary: 'bg-emerald-600 text-white hover:bg-emerald-700',
      secondary: 'border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50',
      accent: 'text-emerald-700',
    },
    amber: {
      shell: 'border-amber-200 bg-gradient-to-r from-amber-50 via-white to-orange-50',
      badge: 'border-amber-200 bg-white text-amber-700',
      primary: 'bg-amber-600 text-white hover:bg-amber-700',
      secondary: 'border-amber-200 bg-white text-amber-700 hover:bg-amber-50',
      accent: 'text-amber-700',
    },
    red: {
      shell: 'border-red-200 bg-gradient-to-r from-red-50 via-white to-rose-50',
      badge: 'border-red-200 bg-white text-red-700',
      primary: 'bg-red-600 text-white hover:bg-red-700',
      secondary: 'border-red-200 bg-white text-red-700 hover:bg-red-50',
      accent: 'text-red-700',
    },
  };

  const styles = toneMap[commercialState?.tone] || toneMap.cyan;
  const statusDateLabel = commercialState?.nextDateLabel || '';
  const countdownLabel = commercialState?.countdownLabel || '';

  return (
    <section className={`rounded-[2rem] border p-6 shadow-sm ${styles.shell}`}>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] ${styles.badge}`}>
              {commercialState?.badge || 'Plan activo'}
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{companyName}</span>
          </div>

          <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-900 md:text-3xl">{commercialState?.title}</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">{commercialState?.description}</p>

          <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-slate-700">
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1">{commercialState?.planMeta?.label || 'Plan actual'}</span>
            {statusDateLabel ? <span className="rounded-full border border-slate-200 bg-white px-3 py-1">{statusDateLabel}</span> : null}
            {countdownLabel ? <span className={`rounded-full border bg-white px-3 py-1 ${styles.accent}`}>{countdownLabel}</span> : null}
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-3 lg:min-w-[280px] lg:items-end">
          <div className="w-full rounded-3xl border border-white/80 bg-white/90 p-4 shadow-sm lg:max-w-[320px]">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Estado del plan</p>
            <p className="mt-2 text-base font-bold text-slate-900">{commercialState?.emphasisLabel || commercialState?.planMeta?.label}</p>
            <p className="mt-2 text-sm text-slate-600">
              {commercialState?.shouldRestrictOperations
                ? 'La operación quedó en pausa hasta reactivar el plan.'
                : 'Conviene resolver la continuidad antes del vencimiento.'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Dashboard({
  onNavigate,
  onStartTutorial,
  tutorialSeen = false,
  companyId = 'default',
  companyName = 'Empresa Base',
  companyModules = null,
  companyPlanSettings = null,
  commercialState = null,
  showPlansPanel = false,
  showMessage,
  currentUserData = null,
}) {
  const [routes, setRoutes] = useState([]);
  const [stockControls, setStockControls] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [dashboardPanelsCollapsed, setDashboardPanelsCollapsed] = useState(false);
  const [showDashboardPanelsHint, setShowDashboardPanelsHint] = useState(false);
  const [recentActivityTickerIndex, setRecentActivityTickerIndex] = useState(0);
  const [showIntroExperience, setShowIntroExperience] = useState(() => !readDashboardIntroSeen(companyId));
  const [introGatePending, setIntroGatePending] = useState(() => !readDashboardIntroSeen(companyId));
  const plansSectionRef = useRef(null);
  const previousShowPlansPanelRef = useRef(false);
  const panelsCollapseTimeoutRef = useRef(null);
  const introAudioRef = useRef(null);
  const introAmbientRef = useRef(null);
  const introGeneratedAudioUrlRef = useRef(null);
  const introAutoAdvanceTimeoutRef = useRef(null);
  const introCountdownIntervalRef = useRef(null);
  const introAudioCleanupRef = useRef(null);
  const [introReadyToContinue, setIntroReadyToContinue] = useState(false);
  const [introCountdown, setIntroCountdown] = useState(INTRO_AUTO_ADVANCE_SECONDS);
  const [introPlaybackStarted, setIntroPlaybackStarted] = useState(false);

  useEffect(() => {
    const introPending = !readDashboardIntroSeen(companyId);
    setShowIntroExperience(introPending);
    setIntroGatePending(introPending);
    setIntroReadyToContinue(false);
    setIntroCountdown(INTRO_AUTO_ADVANCE_SECONDS);
    setIntroPlaybackStarted(false);
  }, [companyId]);

  useEffect(() => {
    if (!db || !companyId) {
      setRoutes([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const routesRef = collection(db, resolveRoutesCollectionPath(companyId));
    const unsubscribe = onSnapshot(
      routesRef,
      (snapshot) => {
        const nextRoutes = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));
        setRoutes(sortRoutesDesc(nextRoutes));
        setLoading(false);
      },
      (error) => {
        console.error('Error loading dashboard routes:', error);
        setRoutes([]);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [companyId]);

  useEffect(() => {
    if (!db || !companyId) {
      setStockControls([]);
      return undefined;
    }

    const controlsRef = collection(db, resolveStockControlsCollectionPath(companyId));
    const controlsQuery = query(controlsRef, where('companyId', '==', companyId));
    const unsubscribe = onSnapshot(
      controlsQuery,
      (snapshot) => {
        const nextControls = snapshot.docs
          .map((docItem) => ({ id: docItem.id, ...docItem.data(), _kind: 'stock' }))
          .sort((a, b) => getTimestampMillis(b?.endTime) - getTimestampMillis(a?.endTime));
        setStockControls(nextControls);
      },
      (error) => {
        console.error('Error loading dashboard stock controls:', error);
        setStockControls([]);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  useEffect(() => {
    if (!db || !companyId) {
      setStockMovements([]);
      return undefined;
    }

    const movementsRef = collection(db, resolveStockMovementsCollectionPath(companyId));
    const unsubscribe = onSnapshot(
      movementsRef,
      (snapshot) => {
        const nextMovements = snapshot.docs
          .map((docItem) => ({ id: docItem.id, ...docItem.data(), _kind: 'stock-movement' }))
          .sort((a, b) => getStockMovementTimestamp(b) - getStockMovementTimestamp(a));
        setStockMovements(nextMovements);
      },
      (error) => {
        console.error('Error loading dashboard stock movements:', error);
        setStockMovements([]);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  useEffect(() => {
    if (!showPlansPanel || previousShowPlansPanelRef.current === showPlansPanel) {
      previousShowPlansPanelRef.current = showPlansPanel;
      return;
    }

    previousShowPlansPanelRef.current = showPlansPanel;

    if (plansSectionRef.current) {
      plansSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showPlansPanel]);

  const resolvedCommercialState = useMemo(() => {
    return commercialState || resolveCommercialState({
      planSettings: companyPlanSettings,
      modulesSettings: companyModules,
    });
  }, [commercialState, companyModules, companyPlanSettings]);

  const enabledModules = useMemo(() => getEnabledModules(companyModules), [companyModules]);
  const enabledModuleKeys = useMemo(() => new Set(enabledModules.map((module) => module.key)), [enabledModules]);
  const commercialRestrictionActive = Boolean(resolvedCommercialState?.shouldRestrictOperations);

  const monitorEnabled = useMemo(
    () => isViewEnabled(companyModules, 'monitor') && !commercialRestrictionActive,
    [commercialRestrictionActive, companyModules]
  );
  const reportsEnabled = useMemo(
    () => isViewEnabled(companyModules, 'reports') && !commercialRestrictionActive,
    [commercialRestrictionActive, companyModules]
  );

  const planMeta = useMemo(
    () => resolvedCommercialState?.planMeta || resolveCompanyPlanMeta({ planSettings: companyPlanSettings, modulesSettings: companyModules }),
    [companyModules, companyPlanSettings, resolvedCommercialState]
  );

  const isTrialPlan = resolvedCommercialState?.isTrial;

  useEffect(() => {
    if (companyPlanSettings?.billingCycle === 'annual' || companyPlanSettings?.billingCycle === 'anual') {
      setBillingCycle('annual');
      return;
    }

    setBillingCycle('monthly');
  }, [companyPlanSettings?.billingCycle]);

  useEffect(() => {
    setDashboardPanelsCollapsed(false);
    setShowDashboardPanelsHint(false);

    if (panelsCollapseTimeoutRef.current) clearTimeout(panelsCollapseTimeoutRef.current);

    panelsCollapseTimeoutRef.current = setTimeout(() => {
      setDashboardPanelsCollapsed(true);
      setShowDashboardPanelsHint(true);
    }, DASHBOARD_AUTO_COLLAPSE_MS);

    return () => {
      if (panelsCollapseTimeoutRef.current) clearTimeout(panelsCollapseTimeoutRef.current);
    };
  }, [companyId]);

  const clearIntroTimers = useCallback(() => {
    if (introAutoAdvanceTimeoutRef.current) {
      window.clearTimeout(introAutoAdvanceTimeoutRef.current);
      introAutoAdvanceTimeoutRef.current = null;
    }
    if (introCountdownIntervalRef.current) {
      window.clearInterval(introCountdownIntervalRef.current);
      introCountdownIntervalRef.current = null;
    }
  }, []);

  const stopIntroPlayback = useCallback(() => {
    clearIntroTimers();
    if (introAudioCleanupRef.current) {
      introAudioCleanupRef.current();
      introAudioCleanupRef.current = null;
    }
    if (introAudioRef.current) {
      introAudioRef.current.pause();
      introAudioRef.current.currentTime = 0;
      introAudioRef.current = null;
    }
    if (introAmbientRef.current) {
      introAmbientRef.current.stop();
      introAmbientRef.current = null;
    }
    if (introGeneratedAudioUrlRef.current) {
      URL.revokeObjectURL(introGeneratedAudioUrlRef.current);
      introGeneratedAudioUrlRef.current = null;
    }
  }, [clearIntroTimers]);

  const finishIntroGuide = useCallback(() => {
    stopIntroPlayback();
    saveDashboardIntroSeen(companyId);
    setIntroGatePending(false);
    setIntroReadyToContinue(false);
    setIntroCountdown(INTRO_AUTO_ADVANCE_SECONDS);
    setIntroPlaybackStarted(false);
    setShowIntroExperience(false);
    onStartTutorial?.();
  }, [companyId, onStartTutorial, stopIntroPlayback]);

  const startIntroPlayback = useCallback(() => {
    if (!showIntroExperience) return;

    stopIntroPlayback();
    const ambient = createDashboardIntroAmbient();
    introAmbientRef.current = ambient;
    setIntroPlaybackStarted(true);
    setIntroReadyToContinue(false);
    setIntroCountdown(INTRO_AUTO_ADVANCE_SECONDS);

    const handleEnded = () => {
      setIntroReadyToContinue(true);
      setIntroCountdown(INTRO_AUTO_ADVANCE_SECONDS);
      introCountdownIntervalRef.current = window.setInterval(() => {
        setIntroCountdown((current) => {
          if (current <= 1) {
            if (introCountdownIntervalRef.current) {
              window.clearInterval(introCountdownIntervalRef.current);
            }
            return 0;
          }
          return current - 1;
        });
      }, 1000);
      introAutoAdvanceTimeoutRef.current = window.setTimeout(finishIntroGuide, INTRO_AUTO_ADVANCE_SECONDS * 1000);
    };

    ambient?.resume();

    const playResolvedAudio = async () => {
      const elevenLabsApiKey = process.env.REACT_APP_ELEVENLABS_API_KEY || '';
      const elevenLabsVoiceId = process.env.REACT_APP_ELEVENLABS_VOICE_ID || ELEVENLABS_DEFAULT_VOICE_ID;
      const elevenLabsModelId = process.env.REACT_APP_ELEVENLABS_MODEL_ID || ELEVENLABS_DEFAULT_MODEL_ID;

      let audioSrc = INTRO_FALLBACK_AUDIO;

      try {
        const generatedUrl = await buildIntroNarrationAudio({
          apiKey: elevenLabsApiKey,
          voiceId: elevenLabsVoiceId,
          modelId: elevenLabsModelId,
          text: INTRO_NARRATION_TEXT,
        });
        if (generatedUrl) {
          introGeneratedAudioUrlRef.current = generatedUrl;
          audioSrc = generatedUrl;
        }
      } catch (error) {
        console.warn('Intro narration fallback to local audio:', error);
      }

      const audio = new Audio(audioSrc);
      introAudioRef.current = audio;
      audio.addEventListener('ended', handleEnded);
      introAudioCleanupRef.current = () => audio.removeEventListener('ended', handleEnded);
      audio.volume = 1;
      audio.play().catch(() => {
        setIntroReadyToContinue(true);
        setIntroCountdown(0);
      });
    };

    playResolvedAudio().catch(() => {
      setIntroReadyToContinue(true);
      setIntroCountdown(0);
    });
  }, [finishIntroGuide, showIntroExperience, stopIntroPlayback]);

  const handleIntroGateStart = () => {
    setIntroGatePending(false);
    startIntroPlayback();
  };

  useEffect(() => {
    return () => {
      stopIntroPlayback();
    };
  }, [stopIntroPlayback]);

  const toggleDashboardPanelsCollapsed = () => {
    if (panelsCollapseTimeoutRef.current) clearTimeout(panelsCollapseTimeoutRef.current);
    setShowDashboardPanelsHint(false);
    setDashboardPanelsCollapsed((value) => !value);
  };

  const handlePlanAction = async (plan) => {
    if (!plan?.key || plan.key === 'trial') return;

    try {
      await addDoc(collection(db, `artifacts/${appId}/billingRequests`), {
        companyId,
        companyName,
        requestedPlanKey: plan.key,
        requestedPlanTitle: plan.title,
        billingCycle,
        status: 'pending',
        source: 'company_dashboard',
        requestedByUid: currentUserData?.uid || null,
        requestedByEmail: currentUserData?.email || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      showMessage?.(
        `Solicitud enviada: plan ${plan.title} ${billingCycle === 'annual' ? 'anual' : 'mensual'}. Queda visible en Arquitectura para revisar pago y activación.`,
        'success',
        7000
      );
    } catch (error) {
      console.error('Error creating billing request:', error);
      showMessage?.('No pudimos enviar la solicitud de plan. Probá nuevamente.', 'error', 7000);
    }
  };

  const handleStartIntroGuide = () => {
    if (!introPlaybackStarted) {
      startIntroPlayback();
      return;
    }

    finishIntroGuide();
  };

  const handleSkipIntro = () => {
    saveDashboardIntroSeen(companyId);
    stopIntroPlayback();
    setIntroGatePending(false);
    setIntroReadyToContinue(false);
    setIntroCountdown(INTRO_AUTO_ADVANCE_SECONDS);
    setIntroPlaybackStarted(false);
    setShowIntroExperience(false);
  };

  const handleToggleIntro = () => {
    const nextValue = !showIntroExperience;
    saveDashboardIntroSeen(companyId, !nextValue);

    if (nextValue) {
      setIntroGatePending(false);
      setShowIntroExperience(true);
      return;
    }

    stopIntroPlayback();
    setIntroGatePending(false);
    setIntroReadyToContinue(false);
    setIntroCountdown(INTRO_AUTO_ADVANCE_SECONDS);
    setIntroPlaybackStarted(false);
    setShowIntroExperience(false);
  };

  const today = getLocalDateAsString(new Date());
  const activeRoutesToday = useMemo(
    () => routes.filter((route) => route?.date === today && isRouteActive(route)),
    [routes, today]
  );
  const recentActivity = useMemo(() => {
    const routeItems = routes.map((route) => ({
      ...route,
      _kind: 'route',
      _sortTime: getRouteSortTime(route)
    }));
    const stockMovementItems = stockMovements.map((movement) => ({
      ...movement,
      _kind: 'stock-movement',
      _sortTime: getStockMovementTimestamp(movement)
    }));
    const stockItems = stockControls.map((control) => ({
      ...control,
      _kind: 'stock',
      _sortTime: getTimestampMillis(control?.endTime)
    }));

    return [...routeItems, ...stockMovementItems, ...stockItems]
      .sort((a, b) => (b._sortTime || 0) - (a._sortTime || 0))
      .slice(0, 5);
  }, [routes, stockControls, stockMovements]);

  useEffect(() => {
    if (!dashboardPanelsCollapsed || recentActivity.length <= 1) {
      setRecentActivityTickerIndex(0);
      return undefined;
    }

    const interval = window.setInterval(() => {
      setRecentActivityTickerIndex((current) => (current + 1) % recentActivity.length);
    }, 2600);

    return () => window.clearInterval(interval);
  }, [dashboardPanelsCollapsed, recentActivity]);

  const lastMovement = useMemo(() => {
    if (!routes.length) return null;
    const ts = getRouteSortTime(routes[0]);
    return ts ? new Date(ts) : null;
  }, [routes]);

  const lastMovementLabel = lastMovement
    ? lastMovement.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Sin movimiento todavía';
  const recentActivityTickerItem = recentActivity[recentActivityTickerIndex] || null;
  const recentActivityTickerPresentation = getRecentActivityTickerPresentation(recentActivityTickerItem);

  const nextRenewalLabel = resolvedCommercialState?.nextDate ? formatShortDate(resolvedCommercialState.nextDate) : '--';
  const renewalSubtitle = resolvedCommercialState?.nextDateLabel
    ? resolvedCommercialState.nextDateLabel
    : resolvedCommercialState?.shouldRestrictOperations
      ? 'La operación quedó en pausa por vencimiento.'
      : 'Sin fecha comercial visible.';

  const moduleCards = [
    {
      viewKey: 'logistics',
      moduleKey: 'logistics',
      title: 'Logística',
      description: 'Unidades, rutas y seguimiento.',
      iconPath: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25',
      colorClasses: { bg: 'bg-gradient-to-br from-emerald-500 to-green-600', iconBg: 'bg-emerald-200', iconText: 'text-emerald-700', titleText: 'text-white', descriptionText: 'text-emerald-100' },
      badge: activeRoutesToday.length > 0 ? `${activeRoutesToday.length} activas` : 'Operación',
    },
    {
      viewKey: 'service',
      moduleKey: 'service',
      title: 'Servicio',
      description: 'Pedidos, programados y contratos.',
      iconPath: 'M3.75 6.75h16.5M6.75 3.75v6m10.5-6v6M4.5 10.5h15A2.25 2.25 0 0121.75 12.75v5.25A2.25 2.25 0 0119.5 20.25h-15A2.25 2.25 0 012.25 18v-5.25A2.25 2.25 0 014.5 10.5z',
      colorClasses: { bg: 'bg-gradient-to-br from-violet-500 to-purple-600', iconBg: 'bg-violet-200', iconText: 'text-violet-700', titleText: 'text-white', descriptionText: 'text-violet-100' },
      badge: 'Comercial',
    },
    {
      viewKey: 'monitor',
      moduleKey: 'routeMonitor',
      title: 'Monitor en Tiempo Real',
      description: 'Seguí las rutas activas y mirá qué está pasando en cada unidad.',
      iconPath: 'M9 17.25v.001M12 17.25v.001M15 17.25v.001M4.5 5.25v13.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V5.25a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25z',
      colorClasses: { bg: 'bg-gradient-to-br from-indigo-500 to-violet-600', iconBg: 'bg-indigo-200', iconText: 'text-indigo-700', titleText: 'text-white', descriptionText: 'text-indigo-100' },
      badge: activeRoutesToday.length > 0 ? `${activeRoutesToday.length} activas` : 'En vivo',
    },
    {
      viewKey: 'reports',
      moduleKey: 'reports',
      title: 'Reportes y liquidación',
      description: 'Revisá historial, liquidaciones y números generales de la empresa.',
      iconPath: 'M3 3.75A.75.75 0 013.75 3h10.5a.75.75 0 01.53.22l3 3a.75.75 0 01.22.53v13.5a.75.75 0 01-.75.75H3.75a.75.75 0 01-.75-.75V3.75zm3.75 4.5h6m-6 3h10.5m-10.5 3h10.5m-10.5 3h6',
      colorClasses: { bg: 'bg-gradient-to-br from-fuchsia-500 to-pink-600', iconBg: 'bg-fuchsia-200', iconText: 'text-fuchsia-700', titleText: 'text-white', descriptionText: 'text-fuchsia-100' },
      badge: routes.length > 0 ? `${routes.length} rutas` : 'Historial',
    },
    {
      viewKey: 'fleetExpenses',
      moduleKey: 'fleetExpenses',
      title: 'Gastos de Flota',
      description: 'Cargá combustible, service y otros gastos de cada transporte.',
      iconPath: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h6m3-3.75l-3.75-3.75A1.125 1.125 0 015.25 6H3.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25h1.5a1.125 1.125 0 01.83.375l3.75 3.75a1.125 1.125 0 010 1.59l-3.75 3.75a1.125 1.125 0 01-.83.375H3.75a2.25 2.25 0 01-2.25-2.25v-1.5a2.25 2.25 0 012.25-2.25h1.5A1.125 1.125 0 005.25 12z',
      colorClasses: { bg: 'bg-gradient-to-br from-amber-500 to-orange-600', iconBg: 'bg-amber-200', iconText: 'text-amber-700', titleText: 'text-white', descriptionText: 'text-amber-100' },
      badge: enabledModuleKeys.has('fleetExpenses') ? 'Seguimiento' : 'Pausado',
    },
    {
      viewKey: 'stock',
      moduleKey: 'stock',
      title: 'Stock y movimientos',
      description: 'Registrá compras, controlá cantidades y prepará salidas.',
      iconPath: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h6.375a.375.375 0 01.375.375v1.5a.375.375 0 01-.375.375H9a.375.375 0 01-.375-.375v-1.5A.375.375 0 019 6.75z',
      colorClasses: { bg: 'bg-gradient-to-br from-sky-500 to-blue-600', iconBg: 'bg-sky-200', iconText: 'text-sky-700', titleText: 'text-white', descriptionText: 'text-sky-100' },
      badge: enabledModuleKeys.has('stock') ? 'Activo' : 'Pausado',
    },
    {
      viewKey: 'systemGuide',
      moduleKey: 'systemGuide',
      title: 'Guía y soporte',
      description: 'Encontrá ayuda clara, recorrido guiado y soporte para saber qué hace cada módulo y cuál es el próximo paso.',
      iconPath: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
      colorClasses: { bg: 'bg-gradient-to-br from-slate-700 to-slate-800', iconBg: 'bg-slate-200', iconText: 'text-slate-700', titleText: 'text-white', descriptionText: 'text-slate-300' },
      badge: 'Guía y soporte',
    },
  ];

  const logisticsModule = moduleCards.find((module) => module.viewKey === 'logistics');
  const serviceModule = moduleCards.find((module) => module.viewKey === 'service');
  const remainingModuleCards = moduleCards.filter((module) => module.viewKey !== 'logistics' && module.viewKey !== 'service');

  const buildModuleRuntime = (module, index = 0) => {
    const disabledByModule = module.viewKey !== 'architect' && !enabledModuleKeys.has(module.moduleKey);
    const disabledByCommercialState = commercialRestrictionActive && module.viewKey !== 'menu';
    const disabled = disabledByModule || disabledByCommercialState;
    const disabledReason = disabledByCommercialState
      ? 'Operación pausada por vencimiento'
      : disabledByModule
        ? 'No disponible en este plan'
        : '';

    return {
      ...module,
      disabled,
      disabledReason,
      shimmerDelay: index * 3,
      active: !disabled && module.viewKey === 'monitor' && activeRoutesToday.length > 0,
      activeLabel: !disabled && module.viewKey === 'monitor' && activeRoutesToday.length > 0 ? 'Seguimiento vivo en curso' : '',
      onClick: () => onNavigate(module.viewKey),
    };
  };

  const logisticsSplitModules =
    logisticsModule && serviceModule
      ? {
          left: buildModuleRuntime(logisticsModule, 0),
          right: buildModuleRuntime(serviceModule, 1),
        }
      : null;

  return (
    <div className="min-h-full bg-slate-100 p-4 md:p-8">
        {showIntroExperience ? (
          <div className="fixed inset-0 z-[2200] flex items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,#dbeafe_0%,#eff6ff_38%,rgba(248,250,252,0.96)_100%)] px-5">
            <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-[10.5vh] bg-[linear-gradient(180deg,rgba(2,6,23,0.98),rgba(2,6,23,0.94)_55%,rgba(2,6,23,0))] animate-[introRevealSoft_1.18s_cubic-bezier(0.22,1,0.36,1)_both]" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-[11vh] bg-[linear-gradient(0deg,rgba(2,6,23,0.98),rgba(2,6,23,0.94)_55%,rgba(2,6,23,0))] animate-[introRevealSoft_1.28s_cubic-bezier(0.22,1,0.36,1)_both]" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(34,211,238,0.12),transparent_26%),radial-gradient(circle_at_82%_14%,rgba(59,130,246,0.14),transparent_24%),radial-gradient(circle_at_68%_78%,rgba(168,85,247,0.08),transparent_24%)]" />
            <div className="pointer-events-none absolute inset-y-0 left-[-18%] w-[42%] bg-[linear-gradient(90deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.82)_42%,rgba(191,219,254,0.52)_70%,rgba(255,255,255,0)_100%)] blur-[10px] animate-[introWaveSweep_3.45s_cubic-bezier(0.22,1,0.36,1)_both]" />
            <div className="pointer-events-none absolute inset-y-0 left-[-12%] w-[26%] bg-[linear-gradient(90deg,rgba(255,255,255,0)_0%,rgba(34,211,238,0.18)_54%,rgba(255,255,255,0)_100%)] blur-2xl animate-[introWaveSweepSoft_4.1s_cubic-bezier(0.22,1,0.36,1)_both]" />
            <div className="pointer-events-none absolute left-[10%] top-[16%] h-36 w-36 rounded-full bg-cyan-300/10 blur-3xl animate-[introAurora_18s_ease-in-out_infinite]" />
            <div className="pointer-events-none absolute right-[10%] top-[12%] h-40 w-40 rounded-full bg-blue-300/10 blur-3xl animate-[introAurora_20s_ease-in-out_infinite_reverse]" />

          {introGatePending ? (
            <div className="relative z-10 mx-auto flex min-h-[72vh] w-full max-w-xl flex-col items-center justify-center px-2 text-center">
              <button
                type="button"
                onClick={handleIntroGateStart}
                className="group relative inline-flex h-44 w-44 items-center justify-center rounded-[42px] border border-cyan-200/70 bg-[linear-gradient(145deg,rgba(2,6,23,0.98),rgba(15,23,42,0.94)_45%,rgba(14,165,233,0.88))] text-white shadow-[0_36px_90px_rgba(15,23,42,0.28),0_0_0_10px_rgba(34,211,238,0.08)] transition duration-500 hover:-translate-y-1 hover:shadow-[0_42px_110px_rgba(15,23,42,0.34),0_0_0_14px_rgba(34,211,238,0.12)]"
              >
                <span className="absolute inset-[-18px] rounded-[54px] border border-cyan-300/30 opacity-80 animate-[introPulse_1.8s_ease-in-out_infinite]" />
                <span className="absolute inset-3 rounded-[34px] border border-white/10 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.2),transparent_34%)]" />
                <span className="relative flex flex-col items-center gap-2">
                  <span className="text-4xl font-black tracking-[-0.08em]">G</span>
                  <span className="text-[11px] font-black uppercase tracking-[0.32em] text-cyan-100">G-CENTRA</span>
                </span>
              </button>
              <p className="mt-8 max-w-sm text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
                Tocá para iniciar la presentación con voz y música
              </p>
            </div>
          ) : (
          <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-center justify-center px-2 text-center">
            <div className="space-y-5">
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <span className="animate-[introRevealSoft_0.86s_cubic-bezier(0.22,1,0.36,1)_both] rounded-full border border-cyan-200/80 bg-cyan-50/90 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.26em] text-cyan-800">
                  Presentación guiada
                </span>
                  <span className="animate-[introRevealSoft_1.02s_cubic-bezier(0.22,1,0.36,1)_both] rounded-full border border-slate-200/80 bg-white/88 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                  Escuchá voz en off
                </span>
              </div>

              <div className="space-y-4">
                  <h1 className="animate-[introRevealSoft_1.08s_cubic-bezier(0.22,1,0.36,1)_both] text-5xl font-black tracking-[-0.08em] text-slate-950 md:text-[5.6rem]">
                  G-CENTRA
                </h1>
                  <div className="mx-auto h-px w-28 animate-[introLineGrow_1.34s_cubic-bezier(0.22,1,0.36,1)_both] bg-gradient-to-r from-transparent via-cyan-500/80 to-transparent" />
                  <p className="mx-auto max-w-4xl overflow-hidden whitespace-normal animate-[introRevealSoft_1.18s_cubic-bezier(0.22,1,0.36,1)_both,introTypewriter_5.2s_steps(90,end)_both] text-lg font-medium leading-8 text-slate-700 md:text-[1.34rem] md:leading-9">
                  {INTRO_NARRATION_TEXT}
                </p>
              </div>

              <div className="mx-auto grid w-full max-w-5xl gap-3 pt-3 sm:grid-cols-2 xl:grid-cols-3">
                {INTRO_MODULE_CARDS.map((item) => (
                  <div
                    key={item.title}
                    className={`animate-[introCardInSoft_1.18s_cubic-bezier(0.22,1,0.36,1)_both] rounded-[22px] border border-white/70 bg-gradient-to-br ${item.tone} px-4 py-3 text-left shadow-[0_12px_28px_rgba(15,23,42,0.08)] backdrop-blur-sm`}
                    style={{ animationDelay: item.delay }}
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Módulo</p>
                    <h3 className="mt-1 text-base font-bold tracking-[-0.03em] text-slate-900">{item.title}</h3>
                    <p className="mt-1.5 text-xs leading-5 text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col items-center justify-center gap-3 pt-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleStartIntroGuide}
                    className={`inline-flex min-h-[54px] animate-[introRevealSoft_1.44s_cubic-bezier(0.22,1,0.36,1)_both] items-center justify-center gap-2 rounded-[18px] bg-[linear-gradient(180deg,#0f172a_0%,#1d4ed8_100%)] px-6 text-sm font-bold text-white shadow-[0_16px_28px_rgba(29,78,216,0.22)] transition hover:-translate-y-0.5 ${introReadyToContinue ? 'animate-[introRevealSoft_1.44s_cubic-bezier(0.22,1,0.36,1)_both,introPulse_1.8s_ease-in-out_infinite]' : ''}`}
                  >
                    <Icon path="M11.25 9l3 3-3 3m-6-3h9m6.75 0a8.25 8.25 0 11-16.5 0 8.25 8.25 0 0116.5 0z" className="h-4 w-4" />
                    {!introPlaybackStarted
                      ? 'Activar voz y musica'
                      : introReadyToContinue
                        ? `Continuar con la guia${introCountdown > 0 ? ` · ${introCountdown}` : ''}`
                        : 'Saltar a la guia'}
                  </button>
                  <button
                    type="button"
                    onClick={handleSkipIntro}
                    className="inline-flex min-h-[54px] animate-[introRevealSoft_1.58s_cubic-bezier(0.22,1,0.36,1)_both] items-center justify-center gap-2 rounded-[18px] border border-slate-200 bg-white/90 px-6 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white"
                  >
                    Entrar sin guia
                  </button>
                </div>

                <p className="animate-[introRevealSoft_1.72s_cubic-bezier(0.22,1,0.36,1)_both] text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                {!introPlaybackStarted
                  ? 'Por seguridad del navegador, la voz empieza cuando tocás activar.'
                  : introReadyToContinue
                    ? 'Si no tocás nada, la guía continúa sola.'
                    : 'La presentación está reproduciendo voz y música de fondo.'}
                </p>
              </div>
          </div>
          )}
        </div>
      ) : null}
      <style>{`
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translate3d(0, 22px, 0); }
          100% { opacity: 1; transform: translate3d(0, 0, 0); }
        }
        @keyframes introReveal {
          0% { opacity: 0; transform: translate3d(0, 34px, 0) scale(0.98); filter: blur(12px); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
        }
        @keyframes introRevealSoft {
          0% { opacity: 0; transform: translate3d(0, 18px, 0) scale(0.992); filter: blur(6px); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
        }
        @keyframes introTypewriter {
          0% { clip-path: inset(0 100% 0 0); }
          100% { clip-path: inset(0 0 0 0); }
        }
        @keyframes introCardIn {
          0% { opacity: 0; transform: translate3d(28px, 18px, 0) scale(0.96); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
        }
        @keyframes introCardInSoft {
          0% { opacity: 0; transform: translate3d(14px, 10px, 0) scale(0.985); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
        }
        @keyframes introPanelIn {
          0% { opacity: 0; transform: translate3d(0, 28px, 0) scale(0.96); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
        }
        @keyframes introLineGrow {
          0% { opacity: 0; transform: scaleX(0.15); transform-origin: left; }
          100% { opacity: 1; transform: scaleX(1); transform-origin: left; }
        }
        @keyframes introAurora {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); opacity: 0.72; }
          50% { transform: translate3d(6px, -8px, 0) scale(1.04); opacity: 0.88; }
        }
        @keyframes introWaveSweep {
          0% { transform: translateX(0) skewX(-12deg); opacity: 0; }
          12% { opacity: 0.95; }
          72% { opacity: 0.88; }
          100% { transform: translateX(320%) skewX(-12deg); opacity: 0; }
        }
        @keyframes introWaveSweepSoft {
          0% { transform: translateX(0) skewX(-10deg); opacity: 0; }
          16% { opacity: 0.75; }
          74% { opacity: 0.52; }
          100% { transform: translateX(360%) skewX(-10deg); opacity: 0; }
        }
        @keyframes introGraphicReveal {
          0% { opacity: 0; transform: translate3d(28px, 0, 0) scale(0.985); filter: blur(10px); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
        }
        @keyframes dashboardIconSweep {
          0%, 12% { transform: translateX(-120%); opacity: 0; }
          16% { opacity: 0.95; }
          28% { transform: translateX(180%); opacity: 0; }
          100% { transform: translateX(180%); opacity: 0; }
        }
        @keyframes introPulse {
          0%, 100% { box-shadow: 0 16px 28px rgba(29,78,216,0.2), 0 0 0 0 rgba(59,130,246,0.12); }
          50% { box-shadow: 0 16px 28px rgba(29,78,216,0.26), 0 0 0 8px rgba(59,130,246,0.05); }
        }
        @keyframes introSignal {
          0%, 100% { transform: scale(1); opacity: 0.7; box-shadow: 0 0 0 0 rgba(103,232,249,0.2); }
          50% { transform: scale(1.08); opacity: 1; box-shadow: 0 0 0 8px rgba(103,232,249,0.08); }
        }
        @keyframes dashboardMonitorGlow {
          0%, 100% { box-shadow: inset 0 0 0 1px rgba(74,222,128,0.08), 0 12px 26px rgba(2,6,23,0.28); }
          50% { box-shadow: inset 0 0 0 1px rgba(74,222,128,0.18), 0 16px 34px rgba(2,6,23,0.36); }
        }
        @keyframes dashboardMonitorSweep {
          0% { transform: translateY(-120%); opacity: 0; }
          18% { opacity: 0.18; }
          100% { transform: translateY(260%); opacity: 0; }
        }
        @keyframes dashboardTickerFade {
          0% { opacity: 0; transform: translateY(6px); }
          12%, 88% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-6px); }
        }
      `}</style>
      <div className="mx-auto max-w-7xl space-y-8">
        {resolvedCommercialState?.isExpired || resolvedCommercialState?.isExpiringSoon ? (
          <CommercialHero
            commercialState={resolvedCommercialState}
            companyName={companyName}
          />
        ) : null}

        {showPlansPanel ? (
          <section
            ref={plansSectionRef}
            className="rounded-[2rem] border border-cyan-300 bg-white p-5 shadow-sm ring-2 ring-cyan-200 transition-all duration-300 md:p-6"
          >
            <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">Planes disponibles</p>
                <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Escalá G-CENTRA según tu etapa operativa</h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  Compará opciones y definí cómo querés continuar con tu operación.
                </p>
              </div>
              {resolvedCommercialState?.countdownLabel ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                  {resolvedCommercialState.countdownLabel}
                </div>
              ) : null}
            </div>

            <CompanyPlanPanel
              companyName={companyName}
              companyPlanMeta={companyPlanSettings}
              isTrial={isTrialPlan}
              onPlanAction={handlePlanAction}
              billingCycle={billingCycle}
              onBillingCycleChange={setBillingCycle}
            />
          </section>
        ) : null}

        <section id="dashboard-summary" className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            title="Base de datos"
            value="Clientes y proveedores"
            subtitle="Y más registros operativos para usar en toda la app."
            iconPath="M3 3.75A.75.75 0 013.75 3h10.5a.75.75 0 01.53.22l3 3a.75.75 0 01.22.53v13.5a.75.75 0 01-.75.75H3.75a.75.75 0 01-.75-.75V3.75zm3.75 4.5h6m-6 3h10.5m-10.5 3h10.5m-10.5 3h6"
            tone="cyan"
            onClick={() => onNavigate('database')}
            valueClassName="leading-tight"
          />
          <SummaryCard
            title="Herramientas disponibles"
            value={enabledModules.length}
            subtitle={commercialRestrictionActive ? 'Cuenta en pausa hasta reactivar el plan.' : 'Funciones activas para tu operación.'}
            iconPath="M3.75 5.25h16.5M3.75 12h16.5M3.75 18.75h16.5"
            tone={commercialRestrictionActive ? 'amber' : 'emerald'}
          />
          <SummaryCard
            title="Rutas activas"
            value={loading ? '...' : activeRoutesToday.length}
            subtitle={
              commercialRestrictionActive
                ? 'La operación está pausada por estado comercial.'
                : loading
                  ? 'Cargando actividad...'
                  : activeRoutesToday.length > 0
                    ? 'Hay movimiento en curso.'
                    : 'No hay recorridos en curso en este momento.'
            }
            iconPath="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125"
            tone={commercialRestrictionActive ? 'red' : 'violet'}
          />
          <SummaryCard
            title={resolvedCommercialState?.shouldRestrictOperations ? 'Estado comercial' : 'Próxima lectura'}
            value={resolvedCommercialState?.countdownLabel || nextRenewalLabel}
            subtitle={resolvedCommercialState?.countdownLabel ? renewalSubtitle : resolvedCommercialState?.helperText || renewalSubtitle}
            iconPath="M12 6v6l4 2.25M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            tone={resolvedCommercialState?.tone || 'slate'}
          />
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.3fr_0.9fr]">
          <div id="recent-activity" className="rounded-[2rem] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] p-4 shadow-[0_20px_46px_rgba(15,23,42,0.08)] md:p-5">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">Panel operativo</p>
                <h2 className="mt-1 text-xl font-black tracking-tight text-slate-900">Actividad reciente</h2>
                <p className="mt-0.5 text-xs text-slate-500">Lectura viva de rutas, stock y movimientos del día.</p>
              </div>
              <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <button
                  type="button"
                  onClick={toggleDashboardPanelsCollapsed}
                  className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 ${showDashboardPanelsHint && dashboardPanelsCollapsed ? 'animate-pulse shadow-[0_0_0_6px_rgba(148,163,184,0.12)]' : ''}`}
                >
                  <Icon path={dashboardPanelsCollapsed ? 'M19.5 14.25L12 6.75l-7.5 7.5' : 'M4.5 9.75L12 17.25l7.5-7.5'} className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onNavigate('monitor')}
                  disabled={!monitorEnabled}
                  className={`inline-flex min-w-0 max-w-[11.5rem] shrink items-center gap-2 rounded-2xl border px-3 py-2 text-[11px] font-semibold leading-none transition-all sm:text-[12px] ${
                    monitorEnabled ? 'border-slate-200 text-slate-700 hover:bg-slate-50' : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                  }`}
                >
                  <Icon path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 truncate whitespace-nowrap">
                    {monitorEnabled ? 'Ver monitor' : 'Monitor no disponible'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onNavigate('reports')}
                  disabled={!reportsEnabled}
                  className={`inline-flex min-w-0 max-w-[11.5rem] shrink items-center gap-2 rounded-2xl border px-3 py-2 text-[11px] font-semibold leading-none transition-all sm:text-[12px] ${
                    reportsEnabled ? 'border-slate-200 text-slate-700 hover:bg-slate-50' : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                  }`}
                >
                  <Icon path="M3 3.75A.75.75 0 013.75 3h10.5a.75.75 0 01.53.22l3 3a.75.75 0 01.22.53v13.5a.75.75 0 01-.75.75H3.75a.75.75 0 01-.75-.75V3.75zm3.75 4.5h6m-6 3h10.5m-10.5 3h10.5m-10.5 3h6" className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 truncate whitespace-nowrap">
                    {reportsEnabled ? 'Ver reportes' : 'Reportes no disponibles'}
                  </span>
                </button>
              </div>
            </div>

            <div
              className={`overflow-hidden transition-all duration-500 ease-out ${
                dashboardPanelsCollapsed ? 'mt-0 max-h-0 opacity-0' : 'mt-4 max-h-[520px] opacity-100'
              }`}
            >
              <div className="space-y-2.5">
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="h-[64px] animate-pulse rounded-2xl bg-slate-100" />
                ))
              ) : recentActivity.length > 0 ? (
                recentActivity.map((item) =>
                  item._kind === 'stock-movement'
                    ? <StockMovementRow key={`stock-movement-${item.id}`} movement={item} />
                    : item._kind === 'stock'
                      ? <StockActivityRow key={`stock-${item.id}`} control={item} />
                      : <RouteRow key={`route-${item.id}`} route={item} />
                )
              ) : (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-slate-500">
                  Todavía no hay actividad registrada.
                </div>
              )}
              </div>
            </div>
            {dashboardPanelsCollapsed ? (
              <div className="relative mt-4 overflow-hidden rounded-[24px] border border-emerald-500/20 bg-[linear-gradient(180deg,#020617_0%,#071226_100%)] px-4 py-3 text-emerald-300 shadow-[0_12px_26px_rgba(2,6,23,0.28)] animate-[dashboardMonitorGlow_4.5s_ease-in-out_infinite]">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(74,222,128,0.08),transparent_42%)]" />
                <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-[linear-gradient(180deg,rgba(74,222,128,0.08),transparent)]" />
                <div className="pointer-events-none absolute left-0 right-0 top-0 h-24 bg-[linear-gradient(180deg,rgba(34,197,94,0.14),rgba(34,197,94,0))] opacity-60 animate-[dashboardMonitorSweep_3.8s_linear_infinite]" />
                <div className="relative z-10 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(74,222,128,0.8)]" />
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-400/90">Monitor resumido</p>
                    </div>
                    <div key={`${recentActivityTickerItem?._kind || 'empty'}-${recentActivityTickerItem?.id || 'none'}-${recentActivityTickerIndex}`} className="mt-3 animate-[dashboardTickerFade_2.4s_ease-in-out_both]">
                      <p className="truncate font-mono text-sm font-bold uppercase tracking-[0.08em] text-emerald-200">
                        {recentActivityTickerPresentation.title}
                      </p>
                      <p className="mt-1 line-clamp-2 font-mono text-xs text-emerald-300/72">
                        {recentActivityTickerPresentation.subtitle}
                      </p>
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                    {recentActivityTickerPresentation.badge}
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          <div id="operation-status" className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm md:p-6">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Estado de la operación</h2>
                <p className="mt-1 text-sm text-slate-500">Panorama actual de tu empresa.</p>
              </div>
              <button
                type="button"
                onClick={toggleDashboardPanelsCollapsed}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 ${showDashboardPanelsHint && dashboardPanelsCollapsed ? 'animate-pulse shadow-[0_0_0_6px_rgba(148,163,184,0.12)]' : ''}`}
              >
                <Icon path={dashboardPanelsCollapsed ? 'M19.5 14.25L12 6.75l-7.5 7.5' : 'M4.5 9.75L12 17.25l7.5-7.5'} className="w-4 h-4" />
              </button>
            </div>

            <div
              className={`overflow-hidden transition-all duration-500 ease-out ${
                dashboardPanelsCollapsed ? 'mt-0 max-h-0 opacity-0' : 'mt-5 max-h-[640px] opacity-100'
              }`}
            >
              <div className="space-y-4">
              <div className="rounded-3xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Operación logística</p>
                <p className="mt-2 text-sm font-medium text-slate-700">
                  {commercialRestrictionActive
                    ? 'La empresa quedó en pausa comercial. El entorno operativo no está habilitado hasta reactivar el plan.'
                    : activeRoutesToday.length > 0
                      ? `${activeRoutesToday.length} ruta${activeRoutesToday.length === 1 ? '' : 's'} con movimiento hoy.`
                      : 'Sin actividad operativa en este momento.'}
                </p>
              </div>

              <div className="rounded-3xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Espacio de trabajo</p>
                <p className="mt-2 text-sm font-medium text-slate-700">
                  Estás operando dentro del panel de <strong>{companyName}</strong>.
                </p>
              </div>
              <div className="rounded-3xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Herramientas disponibles</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {enabledModules.length > 0 ? (
                    enabledModules.map((module) => (
                      <span key={module.key} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                        {module.label}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-slate-500">No hay herramientas disponibles en este momento.</span>
                  )}
                </div>
              </div>
              </div>
            </div>
            {dashboardPanelsCollapsed ? (
              <div className="mt-5 rounded-3xl bg-slate-50 px-4 py-4 text-sm text-slate-600">
                {commercialRestrictionActive
                  ? 'La operación está pausada por estado comercial.'
                  : activeRoutesToday.length > 0
                    ? `${activeRoutesToday.length} ruta${activeRoutesToday.length === 1 ? '' : 's'} con movimiento hoy.`
                    : 'Sin actividad operativa en este momento.'}
              </div>
            ) : null}
          </div>
        </section>

        <section>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 className="text-2xl font-bold text-slate-900">Accesos principales</h2>
              <p className="text-sm text-slate-500">
                {commercialRestrictionActive
                  ? 'Los módulos operativos quedan pausados hasta reactivar el plan.'
                  : 'Todo lo que necesitás para gestionar tu operación desde un solo lugar.'}
              </p>
            </div>
            <button
              type="button"
              onClick={onStartTutorial}
              className={`inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-800 transition-all hover:-translate-y-0.5 hover:bg-cyan-100 ${
                tutorialSeen
                  ? ''
                  : 'animate-pulse shadow-[0_0_0_6px_rgba(34,211,238,0.18),0_0_24px_rgba(34,211,238,0.32)]'
              }`}
            >
              <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="h-4 w-4" />
              Tutorial guiado
            </button>
            <button
              type="button"
              onClick={handleToggleIntro}
              className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold transition-all hover:-translate-y-0.5 ${
                showIntroExperience
                  ? 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className={`inline-flex h-2.5 w-2.5 rounded-full ${showIntroExperience ? 'bg-fuchsia-500' : 'bg-slate-300'}`} />
              Intro {showIntroExperience ? 'On' : 'Off'}
            </button>
          </div>

          <div id="module-cards" className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {logisticsSplitModules ? (
              <SplitModuleCard
                id="logistics-service-card"
                left={logisticsSplitModules.left}
                right={logisticsSplitModules.right}
              />
            ) : null}
            {remainingModuleCards.map((module, index) => {
              const runtimeModule = buildModuleRuntime(module, index + 2);

              return (
                <ModuleCard
                  key={runtimeModule.viewKey}
                  id={runtimeModule.viewKey === 'architect' ? 'architect-card' : undefined}
                  title={runtimeModule.title}
                  description={runtimeModule.disabled ? 'Esta herramienta no está disponible en este momento.' : runtimeModule.description}
                  iconPath={runtimeModule.iconPath}
                  colorClasses={runtimeModule.colorClasses}
                  onClick={runtimeModule.onClick}
                  badge={runtimeModule.badge}
                  shimmerDelay={runtimeModule.shimmerDelay}
                  disabled={runtimeModule.disabled}
                  disabledReason={runtimeModule.disabledReason}
                  active={runtimeModule.active}
                  activeLabel={runtimeModule.activeLabel}
                />
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
